import { onMounted, onUnmounted, toRaw, type Ref } from 'vue';
import { BookCanvas } from '@/modules/editor/canvas/BookCanvas.class';
import { EVENT_NAMES } from '@/constants';
import { emitter } from '@/utils/emitter';
import { createAction, createBookPage } from '@/modules/editor/canvas/models/creators';
import { useProjectStore } from '@/store/project';
import { PlayAction } from '@/modules/editor/timeline/action/PlayAction.class';
import type { ActionData, BookPageData } from '@/types';
import { isPCViewport } from '@/hooks/useViewportAccessGate';

export type UseBookOptions = {
  /** 嵌入编辑器预览时为 true：不创建水印 */
  embedded?: boolean;
  /** 等待阅读器 DOM 完成首次非零尺寸测量，确保 Pixi 不使用挂载过渡尺寸初始化。 */
  waitForInitialSize?: () => Promise<{ width: number; height: number }>;
};

/** 阅读端书本画布 hook：初始化页面、BGM，并按场景决定是否加水印。 */
export const useBook = (canvasRef: Ref<HTMLDivElement | null>, options: UseBookOptions = {}) => {
  const { embedded = false, waitForInitialSize } = options;
  const canvas = new BookCanvas();
  const projectStore = useProjectStore();

  let resolveCanvasReady!: (canvas: BookCanvas) => void;
  let rejectCanvasReady!: (reason?: unknown) => void;
  let initCanvasTask: Promise<void> | null = null;
  let bgmLoadToken = 0;

  const canvasReady = new Promise<BookCanvas>((resolve, reject) => {
    resolveCanvasReady = resolve;
    rejectCanvasReady = reject;
  });

  /** 等待 canvas 容器初始化完成，并统一触发 canvasReady。 */
  const ensureCanvasReady = async () => {
    initCanvasTask ??= initCanvas();
    await initCanvasTask;
    resolveCanvasReady(canvas);
  };

  /** 移动端后台加载 BGM；新任务会使旧任务失效，避免切换工程后挂载过期音频。 */
  const loadBgmInBackground = async (bgmData: ActionData | null | undefined) => {
    const token = ++bgmLoadToken;
    canvas.removeBgm();
    if (!bgmData) return;

    try {
      const bgmAc = await createAction(bgmData, { preferHtml5Audio: true });
      if (token !== bgmLoadToken) {
        bgmAc?.destroy();
        return;
      }

      if (bgmAc instanceof PlayAction) {
        canvas.addBgm(bgmAc);
      } else {
        bgmAc?.destroy();
      }
    } catch (error) {
      if (token === bgmLoadToken) {
        console.error('BGM 加载失败', error);
      }
    }
  };

  /** 加载完整项目数据到阅读画布，并在完成后触发 canvasReady。 */
  const handleProjectLoad = async (params: {
    resolve?: (meta?: any) => void;
    reject?: (err?: any) => void;
  }) => {
    try {
      await ensureCanvasReady();

      // 后续还会有加载背景，bgm等其他操作，所以这里直接调用store里面的数据加载
      const { resolve } = params;

      // 切换工程时先清空旧页面
      canvas.clearElements();

      const deferBgm = !isPCViewport();
      if (!deferBgm && projectStore.bgm) {
        bgmLoadToken += 1;
        const bgmAc = await createAction(projectStore.bgm, { preferHtml5Audio: true });
        if (bgmAc && bgmAc instanceof PlayAction) canvas.addBgm(bgmAc);
      }

      // 1. 先加载底页，再加载元素
      addPages(projectStore.pages);

      resolve?.();
      if (deferBgm) void loadBgmInBackground(projectStore.bgm);
    } catch (error) {
      params.reject?.(error);
      rejectCanvasReady(error);
    }
  };

  /** 编辑器预览复用 projectStore 数据，需重建页面与 BGM 后再通知阅读画布就绪。 */
  const handleProjectPreviewLoad = async (params: {
    resolve?: (meta?: any) => void;
    reject?: (err?: any) => void;
  }) => {
    try {
      await ensureCanvasReady();

      canvas.clearElements();

      const deferBgm = !isPCViewport();
      if (!deferBgm && projectStore.bgm) {
        bgmLoadToken += 1;
        const bgmAc = await createAction(projectStore.bgm, { preferHtml5Audio: true });
        if (bgmAc && bgmAc instanceof PlayAction) canvas.addBgm(bgmAc);
      }

      addPages(projectStore.pages);

      params.resolve?.();
      if (deferBgm) void loadBgmInBackground(projectStore.bgm);
    } catch (error) {
      params.reject?.(error);
      rejectCanvasReady(error);
    }
  };

  /** 监听 BGM 添加/移除，同步到阅读画布。 */
  const handleBgmAdd = async (bgmData: any) => {
    // 1. 无数据时移除当前 BGM
    if (!bgmData) {
      bgmLoadToken += 1;
      canvas.removeBgm();
      return;
    }

    if (!isPCViewport()) {
      void loadBgmInBackground(bgmData);
      return;
    }

    // 2. 创建并挂载 BGM 播放动作
    bgmLoadToken += 1;
    const bgmAc = await createAction(bgmData, { preferHtml5Audio: true });
    if (bgmAc && bgmAc instanceof PlayAction) {
      canvas.addBgm(bgmAc);
    }
  };

  /** 初始化阅读画布相关事件。 */
  const initEvents = () => {
    emitter.on(EVENT_NAMES.PROJECT_LOAD, handleProjectLoad);
    emitter.on(EVENT_NAMES.PROJECT_PREVIEW_LOAD, handleProjectPreviewLoad);
    emitter.on(EVENT_NAMES.BGM_ADD, handleBgmAdd);
  };

  /** 移除阅读画布相关事件，避免组件销毁后继续响应。 */
  const removeEvents = () => {
    emitter.off(EVENT_NAMES.PROJECT_LOAD, handleProjectLoad);
    emitter.off(EVENT_NAMES.PROJECT_PREVIEW_LOAD, handleProjectPreviewLoad);
    emitter.off(EVENT_NAMES.BGM_ADD, handleBgmAdd);
  };

  /** 同步注册页面到 pageMap（不加载内容），内容随可视窗口懒加载。 */
  const addPages = (pageDatas: BookPageData[]) => {
    if (!pageDatas || pageDatas.length === 0) return;

    pageDatas.forEach((data) => {
      // 深拷贝避免 bakeDisplaySize 修改 layout 时影响原始数据
      const page = createBookPage(JSON.parse(JSON.stringify(toRaw(data))));
      page.updateMode('view');
      canvas.addPage(page);
    });

    // 步骤1：独立阅读/分享页才加水印；编辑器内嵌预览跳过，避免影响购买意愿
    if (!embedded && projectStore.enable_watermark) canvas.setPageWatermark(true);

    canvas.setCurrentPage(projectStore.currentPageId);
  };

  onMounted(async () => {
    try {
      initEvents();
      await ensureCanvasReady();
      addPages(projectStore.pages);
    } catch (error) {
      rejectCanvasReady(error);
      throw error;
    }
  });

  onUnmounted(() => {
    destroy();
  });

  const initCanvas = async () => {
    const initialSize = await waitForInitialSize?.();
    const container = canvasRef.value;
    if (!container) throw new Error('Book canvas container is not mounted.');

    await canvas.init(container);
    if (initialSize) {
      canvas.syncViewportSize(initialSize.width, initialSize.height);
    }
  };

  const destroy = () => {
    bgmLoadToken += 1;
    removeEvents();
    canvas.destroy();
  };

  const getCanvas = () => canvas;

  return { getCanvas, canvasReady };
};
